/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {SpecialOrderService} from '../../../api/ms-specialorders/specialorder/specialorder.service';
import {StatusBar} from '../../../shared/status-bar/status-bar.model';
import {
    ReplaySubject,
    Subscription
} from 'rxjs';
import {SpoDetailService} from '../../../api/ms-specialorders/specialorder/spo-detail.service';
import {SpecialOrderSummaryModel} from '../../../api/ms-specialorders/contracts/special-order-summary.model';
import {SpoListItemModel} from './spo-list-item.model';
import {SpecialOrderResultModel} from '../../../api/ms-specialorders/contracts/special-order-result.model';
import {SortDirection} from '../../../api/generic/contracts/sort-direction.enum';
import {SpecialOrderMaterialStatusGroupModel} from '../../../api/ms-specialorders/contracts/special-order-material-status-group.model';
import {AvailableActions} from '../../../api/ms-specialorders/contracts/available-actions.enum';
import {Router} from '@angular/router';
import {SpecialOrderStatus} from '../../../api/ms-specialorders/contracts/special-order-status.enum';
import {ReasonService} from '../../../api/ms-specialorders/reason/reason.service';
import {CodeFilterModel} from '../../../shared/filter/code-filter/code-filter.model';
import {SpecialOrderStatusService} from '../../../api/ms-specialorders/specialorder/specialorder-status.service';
import {SpecialOrderStatusBaseModel} from '../../../api/ms-specialorders/contracts/special-order-status-base.model';
import {SpoListFilterColumnEnum} from './spo-list-filter-column.enum';
import {MaterialService} from '../../../api/ms-specialorders/material/material.service';
import {SpecialOrderMaterialStatusBaseModel} from '../../../api/ms-specialorders/contracts/special-order-material-status-base.model';
import {PartnerService} from '../../../api/ms-specialorders/partner/partner.service';
import {PartnerGroupBaseModel} from '../../../api/ms-specialorders/contracts/partner-group-base.model';
import {PartnerBaseModel} from '../../../api/ms-specialorders/contracts/partner-base.model';
import {UserService} from '../../../shared/services/user.service';
import {Permission} from '../../../shared/utils/permission.enum';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {ReasonBaseModel} from '../../../api/ms-specialorders/contracts/reason-base.model';
import {AppService} from '../../../app.service';
import {SpecialOrderMaterialAlertsGroupModel} from '../../../api/ms-specialorders/contracts/special-order-material-alerts-group.model';
import {SpecialOrderContextMenuModel} from '../../models/special-order-context-menu.model';
import {SpoActionsUtilService} from '../../services/spo-actions-util/spo-actions-util.service';
import {SpecialOrderMaterialStatus} from '../../../api/ms-specialorders/contracts/special-order-material-status.enum';
import {SpoSearchCriteriaService} from '../../services/spo-search-criteria.service';
import {SpecialOrderQueryModel} from '../../../api/ms-specialorders/contracts/special-order-query.model';
import {CodeFilterComponent} from '../../../shared/filter/code-filter/code-filter.component';
import {DateFilterComponent} from '../../../shared/filter/date-filter/date-filter.component';
import {InputAndCodeFilterComponent} from '../../../shared/filter/input-and-code-filter/input-and-code-filter.component';
import {InputAndNumberFilterComponent} from '../../../shared/filter/input-and-number-filter/input-and-number-filter.component';
import {AbstractFilterStateModel} from '../../../shared/filter/abstract-filter-state.model';
import {InputAndCodeFilterStateModel} from '../../../shared/filter/input-and-code-filter/input-and-code-filter-state.model';
import {InputAndNumberFilterStateModel} from '../../../shared/filter/input-and-number-filter/input-and-number-filter-state.model';
import {CodeFilterStateModel} from '../../../shared/filter/code-filter/code-filter-state.model';
import {DateFilterStateModel} from '../../../shared/filter/date-filter/date-filter-state.model';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';
import {ClaimListFilterColumnEnum} from "../../../claim/claim-main-page/claim-list/claim-list-filter-column.enum";
import {SupplyingPackingPlantBaseModel} from "../../../api/ms-claims/contracts/supplying-packing-plant-base.model";
import {ReferenceDataApi} from "../../../api/ms-claims/reference-data.api";
import {PrettyPrintReferencePipe} from "../../../shared/pipes/pretty-print-reference/pretty-print-reference.pipe";

@Component({
    selector: 'app-spo-list',
    templateUrl: './spo-list.component.html',
    styleUrls: ['./spo-list.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SpoListComponent implements OnInit, OnDestroy, AfterViewInit {

    /** Filter template variables **/
    @ViewChild('partnerFilterComponent') partnerFilterComponent: CodeFilterComponent;
    @ViewChild('supplyingPackingPlantFilterComponent') supplyingPackingPlantFilterComponent: CodeFilterComponent;
    @ViewChild('numberFilterComponent') numberFilterComponent: InputAndCodeFilterComponent;
    @ViewChild('materialsFilterComponent') materialsFilterComponent: InputAndNumberFilterComponent;
    @ViewChild('reasonFilterComponent') reasonFilterComponent: CodeFilterComponent;
    @ViewChild('changedOnFilterComponent') changedOnFilterComponent: DateFilterComponent;
    @ViewChild('requestedDateFilterComponent') requestedDateFilterComponent: DateFilterComponent;
    @ViewChild('sentDateFilterComponent') sentDateFilterComponent: DateFilterComponent;
    @ViewChild('specialOrderMaterialStatusFilterComponent') specialOrderMaterialStatusFilterComponent: CodeFilterComponent;
    @ViewChild('materialAlertsFilterComponent') materialAlertsFilterComponent: CodeFilterComponent;
    @Output() reportCriteria = new EventEmitter<SpecialOrderQueryModel>();
    /** Set initial ordering arrow values */
    public partnerArrowUp = false;
    public supplyingPackingPlantArrowUp = false;
    public numberArrowUp = false;
    public materialsArrowUp = false;
    public reasonArrowUp = false;
    public changedOnArrowUp = false;
    public requestedDateArrowUp = false;
    public sentDateArrowUp = false;
    /** Set initial SPO list values */
    public specialOrderItems: SpoListItemModel[] = [];
    /** Enums variables */
    public FilterColumnsEnum = SpoListFilterColumnEnum;
    public specialOrderStatus = SpecialOrderStatus;
    public isLoading = false;
    /** Filter options variables */
    public materialAlertsOptions: CodeFilterModel[] = [
        {name: 'Not empty', code: 'ALERT', active: true, checked: true}
    ];
    public reasonFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public supplyingPackingPlantFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public partnerFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public statusFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);
    public materialStatusFilterSubject = new ReplaySubject<CodeFilterModel[]>(1);

    public showClearAllFilters: boolean;
    /** Default inner width */
    public innerWidth: number;
    /** Variable for table spinner with background overlay*/
    public tableIsLoading: boolean;
    public Permission = Permission;
    /** Warnings Label **/
    public requiredForSendLabel = 'Required for send';
    WarningTypeEnum = WarningsEnum;
    /** Subscriptions variable */
    private subscriptions: Subscription = new Subscription();

    constructor(private specialOrderService: SpecialOrderService,
                private specialOrderStatusService: SpecialOrderStatusService,
                private materialService: MaterialService,
                private spoDetailService: SpoDetailService,
                private router: Router,
                private reasonService: ReasonService,
                private referenceDataApi: ReferenceDataApi,
                private partnerService: PartnerService,
                private prettyPrintReferencePipe: PrettyPrintReferencePipe,
                private spinnerService: SpinnerService,
                private userService: UserService,
                private appService: AppService,
                public spoActionsUtilService: SpoActionsUtilService,
                public spoSearchCriteriaService: SpoSearchCriteriaService) {
        this.subscriptions.add(this.spinnerService
                .onGlobalChange()
                .subscribe((isLoading) => {
                    this.isLoading = isLoading;
                }));

        // TODO: CTWILS-2460 - Leaving it as-is as another example, but the observable can be used directly on the HTML. Check AppComponent.
        this.subscriptions.add(this.spinnerService
                .onLocalChange(SpoListComponent.prototype.getSpecialOrders)
                .subscribe((isLoading) => {
                    this.tableIsLoading = isLoading;
                }));

        this.spinnerService.start(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, null);
    }

    ngOnInit(): void {
        this.getSpecialOrders();

        /** Subscription to handle table loading **/
        this.subscriptions.add(
                this.spoActionsUtilService.enableLoading.subscribe((enableLoading) => {
                    this.tableIsLoading = enableLoading;
                })
        );

        this.subscriptions.add(this.appService.onWindowResize.subscribe(() => {
            this.onResize();
        }));

        /** On window resize */
        this.onResize();
    }

    ngAfterViewInit(): void {
        this.restoreFilters();
    }

    onResize(): void {
        this.innerWidth = window.innerWidth;
    }

    /**
     * Get the SPO from the BE with filter and ordering criteria
     * {@link mapItems} maps the results to {@link SpoListItemModel}
     * {@link changePage} keeps the selected page number
     * {@link emitReportCriteria} to update the SPO report search and sort criteria
     */
    getSpecialOrders(hasPaginationCriteria?: boolean): void {
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, SpoListComponent.prototype.getSpecialOrders);
        const spoQuery = this.spoSearchCriteriaService.getSpoQuery(hasPaginationCriteria);

        this.subscriptions.add(
                this.specialOrderService.getSpecialOrders(spoQuery)
                        .subscribe((specialOrderResult) => {
                            this.mapItems(specialOrderResult);
                        })
                        .add(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, SpoListComponent.prototype.getSpecialOrders);
                        })
        );
        this.emitReportCriteria(spoQuery);
    }

    restoreFilters(): void {
        this.partnerFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.PARTNER) as CodeFilterStateModel);
        this.supplyingPackingPlantFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.SUPPLYING_PACKING_PLANT) as CodeFilterStateModel);
        this.numberFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.NUMBER) as InputAndCodeFilterStateModel);
        this.sentDateFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.SENT_DATE) as DateFilterStateModel);
        this.materialsFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.NUMBER_OF_MATERIALS) as
                        InputAndNumberFilterStateModel);
        this.reasonFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.REASON) as CodeFilterStateModel);
        this.changedOnFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.LAST_CHANGE_DATETIME) as DateFilterStateModel);
        this.requestedDateFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.REQUESTED_DATE) as DateFilterStateModel);
        this.specialOrderMaterialStatusFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.SPO_MAT_STATUS) as CodeFilterStateModel);
        this.materialAlertsFilterComponent?.initFilter(
                this.spoSearchCriteriaService.stateMap.get(SpoListFilterColumnEnum.MATERIAL_ALERTS) as CodeFilterStateModel);
        this.setShowClearAllFilters();
        this.arrowToggle();
    }

    ngOnDestroy(): void {
        this.spinnerService.stop(SpinnerContextTypeEnum.GLOBAL, this.constructor);
        /** Unsubscribe to all subscriptions */
        this.subscriptions.unsubscribe();
    }

    /**
     * Emits the SPO report criteria
     * @param specialOrderQueryModel The search criteria for the report
     * **/
    emitReportCriteria(specialOrderQueryModel: SpecialOrderQueryModel): void {
        this.reportCriteria.emit(specialOrderQueryModel);
    }

    /**
     * Creates the status bar for a SPO
     * @param spoMaterialStatusGroups The SPO material status group
     */
    retrieveIStatusBar(spoMaterialStatusGroups: SpecialOrderMaterialStatusGroupModel[]): StatusBar[] {
        const statusBars = [] as StatusBar[];
        if (spoMaterialStatusGroups) {
            for (let i = 0; i < spoMaterialStatusGroups.length; i++) {
                const statusBarMaterial: StatusBar = {
                    code: spoMaterialStatusGroups[i].status.code,
                    description: spoMaterialStatusGroups[i].status.description,
                    value: spoMaterialStatusGroups[i].numberOfMaterials
                };
                statusBars.push(statusBarMaterial);
            }
        }
        return statusBars;
    }

    /**
     * Opens quick view for a selected SPO
     * @param listItem SpoListItemModel of the selected SPO
     */
    accordionChangeState(listItem: SpoListItemModel): void {
        if (!listItem.isOpen && !listItem.isTouched) {
            listItem.isOpen = true;
            const subscription = this.spoDetailService
                    .getSpecialOrderDetail(listItem.specialOrder.number)
                    .subscribe(
                            (res) => {
                                listItem.specialOrder.specialOrderMaterials = res.specialOrderMaterials;
                                listItem.isTouched = true;
                                listItem.isOpen = true;
                            }, (error) => {
                                listItem.isTouched = false;
                                listItem.isOpen = false;
                            });
            this.subscriptions.add(subscription);
        } else {
            listItem.isTouched = true;
            listItem.isOpen = !listItem.isOpen;
        }
    }

    /**
     * Deletes a SPO
     * @param spoNumber The SPO number to be deleted
     */
    deleteSpo(spoNumber: string, canDelete: boolean): void {
        if (canDelete) {
            this.spoActionsUtilService.deleteSpecialOrder(spoNumber,
                    () => this.changePage(this.spoSearchCriteriaService.pageNumber));
        }
        return;
    }

    /**
     * Changes the SPO list page and gets the corresponding SPOs
     * @param pageNumber The page that the list will change to
     */
    changePage(pageNumber: number): void {
        this.spoSearchCriteriaService.pageNumber = pageNumber;

        setTimeout(() => {
            this.getSpecialOrders(true);
        });
    }

    /**
     * Determines if a SPO has a quick view
     * @param specialOrderSummaryModel SPO summary model
     */
    showQuickView(specialOrderSummaryModel: SpecialOrderSummaryModel): boolean {
        return specialOrderSummaryModel ? specialOrderSummaryModel.numberOfMaterials > 0 : false;
    }

    /**
     * Maps SpecialOrderResultModel to SpoListItemModel and sets it to the {@link specialOrderItems}
     * Sets the {@link totalResults} value
     * @param specialOrderResult SPO list to be mapped
     */
    mapItems(specialOrderResult: SpecialOrderResultModel): void {
        this.spoSearchCriteriaService.totalResults = specialOrderResult.totalResults;
        if (specialOrderResult.specialOrdersPage != null) {
            this.specialOrderItems = specialOrderResult.specialOrdersPage.map((specialOrder) => ({
                specialOrder: specialOrder,
                isOpen: false,
                isTouched: false,
                contextMenuOptions: this.mapAvailableActions(specialOrder)
            } as SpoListItemModel));
        } else {
            this.specialOrderItems = [];
        }
    }

    /**
     * Maps the {@link SpecialOrderSummaryModel.availableActions} to {@link SpecialOrderContextMenuModel}
     * @param specialOrder The special order {@link SpecialOrderSummaryModel}
     * @return SpecialOrderContextMenuModel the corresponding {@link SpecialOrderContextMenuModel}
     */
    mapAvailableActions(specialOrder: SpecialOrderSummaryModel): SpecialOrderContextMenuModel {
        if (specialOrder.availableActions != null) {
            return {
                canCreate: specialOrder.availableActions.includes(AvailableActions.CREATE),
                canEdit: specialOrder.availableActions.includes(AvailableActions.EDIT)
                        || specialOrder.availableActions.includes(AvailableActions.EDIT_POSITION_CANCEL)
                        || specialOrder.availableActions.includes(AvailableActions.EDIT_POSITION_DELETE),
                canSubmit: specialOrder.availableActions.includes(AvailableActions.SUBMIT),
                canDelete: specialOrder.availableActions.includes(AvailableActions.DELETE),
                canRetry: specialOrder.availableActions.includes(AvailableActions.RESUBMIT)
            };
        }
        return {
            canCreate: false,
            canEdit: false,
            canDelete: false,
            canSubmit: false,
            canRetry: false
        };
    }

    /**
     * Determines which criteria the SPO list should be ordered
     * @param columnSelected The ordering criteria
     * @param arrowUp If the corresponding arrow orientation is up
     */
    sortingBy(columnSelected: SpoListFilterColumnEnum, arrowUp: boolean): void {
        if (this.spoSearchCriteriaService.sortedBy === columnSelected && !arrowUp) {
            this.spoSearchCriteriaService.sortDirection = SortDirection.ASC;
        } else {
            this.spoSearchCriteriaService.sortDirection = SortDirection.DESC;
        }
        this.spoSearchCriteriaService.sortedBy = columnSelected;
        this.getSpecialOrders(true);
    }

    /**
     * Navigates to the SPO edit page from the context menu
     * @param specialOrderNumber The selected SPO number
     */
    contextMenuEdit(specialOrderNumber: string): void {
        this.router.navigate(['/', 'spo', specialOrderNumber, 'edit']);
    }

    /**
     * Send an SPO from the context menu
     * {@link changePage} keeps the selected page number
     * @param specialOrder The selected SPO
     * @param contextMenuOptions The selected SPO context menu options
     */
    contextMenuSend(specialOrder: SpecialOrderSummaryModel, contextMenuOptions: SpecialOrderContextMenuModel): void {
        const draft = specialOrder != null && specialOrder.materialStatusGroups != null
                ? specialOrder.materialStatusGroups.find((s) => s.status.code === SpecialOrderMaterialStatus.DRAFT)
                : null;
        const draftCount = draft != null ? draft.numberOfMaterials : 0;
        this.spoActionsUtilService.submitSpecialOrder(specialOrder.number, draftCount,
                contextMenuOptions.canSubmit,
                contextMenuOptions.canRetry,
                () => this.changePage(this.spoSearchCriteriaService.pageNumber));
    }

    /**
     * Determines if an SPO context menu action send should be disabled
     * @param specialOrderContextMenu The SPO context menu model
     * @return boolean If the send action should be disabled
     * */
    disableContextMenuSendAction(specialOrderContextMenu: SpecialOrderContextMenuModel): boolean {
        return !specialOrderContextMenu.canSubmit && !specialOrderContextMenu.canRetry;
    }

    /**
     * Toggles ordering arrows
     */
    arrowToggle(): void {
        this.partnerArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.PARTNER);
        this.supplyingPackingPlantArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
            && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.SUPPLYING_PACKING_PLANT);
        this.numberArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.NUMBER);
        this.materialsArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.NUMBER_OF_MATERIALS);
        this.reasonArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.REASON);
        this.changedOnArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.LAST_CHANGE_DATETIME);
        this.requestedDateArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.REQUESTED_DATE);
        this.sentDateArrowUp = (this.spoSearchCriteriaService.sortDirection === SortDirection.ASC
                && this.spoSearchCriteriaService.sortedBy === SpoListFilterColumnEnum.SENT_DATE);
    }

    /**
     * Set material number filter *
     * {@link changePage} keeps the selected page number
     * @param state The emitted code filter state
     */
    onFilterChange(state: AbstractFilterStateModel): void {
        this.spoSearchCriteriaService.setState(state.filterName as SpoListFilterColumnEnum, state, () => {
            this.getSpecialOrders();
            this.setShowClearAllFilters();
        });
    }

    /**
     * Gets the partner group options for the partner filter
     */
    getPartnerGroupOptions(): void {
        const subscription = this.partnerService.getPartnersGroupOptions()
                .subscribe((data: PartnerGroupBaseModel[]) => {
                    this.partnerFilterSubject.next(data.map((partnerGroup: PartnerGroupBaseModel) => {
                        const codeFilter = {
                            name: partnerGroup.country.name,
                            code: partnerGroup.country.alpha2Code,
                            active: partnerGroup.country.active,
                            checked: true,
                            indeterminate: false,
                            children: []
                        } as CodeFilterModel;

                        partnerGroup.partners.forEach((partner: PartnerBaseModel) => {
                            codeFilter.children.push({
                                name: partner.name,
                                code: partner.code,
                                active: partner.active,
                                checked: true,
                                indeterminate: false,
                                father: codeFilter
                            } as CodeFilterModel);
                        });
                        return codeFilter;
                    }));
                });
        this.subscriptions.add(subscription);
    }

    getSupplyingPackingPlants = (): void => {
        const subscription = this.referenceDataApi.getSupplyingPackingPlantsGroupOptions()
            .subscribe((supplyingPackingPlants: SupplyingPackingPlantBaseModel[]) => {
                this.supplyingPackingPlantFilterSubject.next(supplyingPackingPlants.map(supplyingPackingPlant => ({
                    code: supplyingPackingPlant.code,
                    name: supplyingPackingPlant.name,
                    active: supplyingPackingPlant.active,
                    checked: true,
                    tooltipText: this.prettyPrintReferencePipe.transform(supplyingPackingPlant)
                } as CodeFilterModel)));
            });
        this.subscriptions.add(subscription);
    };

    /**
     * Gets the reason options for the reason filter
     */
    getReasonOptions(): void {
        const subscription = this.reasonService.getAllReasonOptions()
                .subscribe((data: ReasonBaseModel[]) => {
                    this.reasonFilterSubject.next(data.map((value) => ({
                        code: value.code,
                        name: value.name,
                        active: value.active,
                        checked: true
                    } as CodeFilterModel)));
                });
        this.subscriptions.add(subscription);
    }

    /**
     * Gets the special order status options for the special order status filter
     */
    getSpecialOrderStatusOptions(): void {
        const subscription = this.specialOrderStatusService.getAllSpecialOrderStatus()
                .subscribe((data: SpecialOrderStatusBaseModel[]) => {
                    this.statusFilterSubject.next(data.map((sos) => ({
                        name: sos.description,
                        code: sos.code,
                        active: sos.active,
                        checked: true
                    } as CodeFilterModel)));
                });
        this.subscriptions.add(subscription);
    }

    /**
     * Gets the material status options for the SPO number filter
     */
    getSpecialOrderMaterialStatusOptions(): void {
        const subscription = this.materialService.getAllMaterialStatus()
                .subscribe((data: SpecialOrderMaterialStatusBaseModel[]) => {
                    this.materialStatusFilterSubject.next(data.map((soms) => ({
                        name: soms.description,
                        code: soms.code,
                        active: soms.active,
                        checked: true
                    } as CodeFilterModel)));
                });
        this.subscriptions.add(subscription);
    }

    hasPartnerColumn(): boolean {
        return this.userService.isMultiPartner() || this.userService.hasPermissions([Permission.ADMIN_SPO_VIEW]);
    }

    /** Clears all filters **/
    clearAllFilters(): void {
        this.spoSearchCriteriaService.clear();
        this.setAllFiltersInactive();
        this.getSpecialOrders();
        this.setShowClearAllFilters();
    }

    /** Sets all filters as inactive **/
    setAllFiltersInactive(): void {
        if (this.hasPartnerColumn()) {
            this.partnerFilterComponent?.resetFilter();
        }
        this.supplyingPackingPlantFilterComponent?.resetFilter();
        this.numberFilterComponent?.resetFilter();
        this.sentDateFilterComponent?.resetFilter();
        this.materialsFilterComponent?.resetFilter();
        this.reasonFilterComponent?.resetFilter();
        this.changedOnFilterComponent?.resetFilter();
        this.requestedDateFilterComponent?.resetFilter();
        this.specialOrderMaterialStatusFilterComponent?.resetFilter();
        this.materialAlertsFilterComponent?.resetFilter();
    }

    /** Sets the visibility of the clear all filters button **/
    setShowClearAllFilters(): void {
        this.showClearAllFilters = (this.hasPartnerColumn() && this.partnerFilterComponent?.isFilterActive)
                || this.supplyingPackingPlantFilterComponent?.isFilterActive
                || this.numberFilterComponent?.isFilterActive
                || this.sentDateFilterComponent?.isFilterActive
                || this.materialsFilterComponent?.isFilterActive
                || this.reasonFilterComponent?.isFilterActive
                || this.changedOnFilterComponent?.isFilterActive
                || this.requestedDateFilterComponent?.isFilterActive
                || this.specialOrderMaterialStatusFilterComponent?.isFilterActive
                || this.materialAlertsFilterComponent?.isFilterActive;
    }

    getSpoMaterialListTableColspan(): number {
        return this.hasPartnerColumn() ? 10 : 9;
    }

    hasAlertsText(alerts: any[]): boolean {
        return alerts != null && alerts.length > 0;
    }

    getMaterialAlertsTooltip(alertsGroups: SpecialOrderMaterialAlertsGroupModel[]): string {
        let count = 0;

        if (alertsGroups != null && alertsGroups.length > 0 && alertsGroups[0] != null) {
            count = alertsGroups[0].count;
        }

        if (count === 1) {
            return `There is ${count} material with alerts in this SPO.`;
        }

        return `There are ${count} materials with alerts in this SPO.`;
    }

    getMaterialAlertsText(alertsGroups: SpecialOrderMaterialAlertsGroupModel[]): string {
        let count = 0;

        if (alertsGroups != null && alertsGroups.length > 0 && alertsGroups[0] != null) {
            count = alertsGroups[0].count;
        }

        if (count === 1) {
            return `${count} material`;
        }

        return `${count} materials`;
    }

    showPagination = (): boolean => this.spoSearchCriteriaService.totalResults > this.spoSearchCriteriaService.pageSize;
    protected readonly ClaimListFilterColumnEnum = ClaimListFilterColumnEnum;
}
